import React, { ChangeEvent, MutableRefObject, useCallback, useEffect, useState } from 'react';
import { Snack, Snackbar, SnackbarProvider, Typography } from 'fronton-react';
import { useStoreon } from 'storeon/react';
import log from 'loglevel';
import { load } from 'recaptcha-v3';
import { sendGaSubscribedEvent } from '../../legacy-webcomponents/uc-footer-subscribe/utils/ga-events';
import { getQaCookies } from '../../utils';
import { api, ApiRequestConfig } from '../../api';
import { State } from '../../store/types';
import { errorInput, focusedInput, FooterSubscribeWrap, policyTypography, subscribeHeader } from './styles';
import { FooterSubscribeProps, SnackType } from './types';
import { errorSnack, successSnack } from './snacks';
import { validateEmail } from '../../utils/validate-email';
import { SubscribeGroup } from './subscribe-group';
import { getIsNewTheme } from '../../selectors';

export const DATA_QA_FOOTER_SUBSCRIBE_SNACKBAR = 'footer-subscribe-snackbar';
export const DATA_QA_FOOTER_SUBSCRIBE_SNACK = 'footer-subscribe-snack';

export const RECAPTCHA_ERROR_MSG = 'Модуль recaptcha-v3 не загружен';
export const SESSION_TOKEN_ERROR_MSG = 'Не удалось получить session_token';

export const FooterSubscribe: React.FC<FooterSubscribeProps> = ({
  subscribersUrl,
  subscribersApiKey,
  recaptchaPublicKey,
}) => {
  const { requestID } = useStoreon<State>('requestID');
  // useSnackbar somehow doesn't work therefore I used useState
  const [currentSnack, setSnack] = useState<SnackType | null>(null);
  const [inputError, setInputError] = useState('');
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const [, setSubscribed] = useState(false);
  const [sessionToken, setSessionToken] = useState('');
  const [groupClass, setGroupClass] = useState('');
  const { cookies } = useStoreon<State>('cookies', 'logoProUrl');
  const isNewTheme = getIsNewTheme(cookies);

  const onFocus = async () => {
    setFocused(true);
    if (!sessionToken) {
      try {
        const recaptcha = await load(recaptchaPublicKey, { autoHideBadge: true });
        const token = await recaptcha.execute('homepage');
        setSessionToken(token);
      } catch (error) {
        log.error(error, RECAPTCHA_ERROR_MSG);
      }
    }
  };

  const onSubmit = (inputRef: MutableRefObject<HTMLInputElement | null>) => async (e: {
    preventDefault: () => void;
  }) => {
    e.preventDefault();
    setInputError('');
    if (!value) {
      setInputError('Укажите адрес эл. почты');
      return;
    }

    if (!validateEmail(value)) {
      log.error('Не email');
      setInputError('Неверный формат эл. почты');
      return;
    }

    if (!sessionToken) {
      log.error(SESSION_TOKEN_ERROR_MSG);
      setSnack({ id: `error-${Math.random()}`, ...errorSnack });
      setTimeout(() => setSnack(null), 3000);
      return;
    }

    let url = subscribersUrl;
    const options = {
      useCredentials: true,
      apiKey: subscribersApiKey,
      requestID,
    };
    const qaCookie = getQaCookies(document?.cookie);

    if (qaCookie) {
      const params = new URLSearchParams({ testId: qaCookie.split('=')[1] }).toString();
      url += `?${params}`;
    }
    try {
      const response = await api.post(
        url,
        {
          contact: value,
          gtoken: sessionToken,
        },
        options as ApiRequestConfig
      );
      inputRef?.current?.blur();
      setSubscribed(true);
      sendGaSubscribedEvent();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (response?.message === 'Client already subscribed') {
        setInputError('Вы уже подписаны');
      } else {
        setSnack({ id: `success-${Math.random()}`, ...successSnack });
        setTimeout(() => setSnack(null), 3000);
      }
    } catch (error) {
      inputRef?.current?.blur();
      setSnack({ id: `error-${Math.random()}`, ...errorSnack });
      setTimeout(() => setSnack(null), 3000);
    } finally {
      setValue('');
      setSessionToken('');
    }
  };
  const onMailInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>, inputValue: string) => {
      setValue(inputValue);
      setInputError('');
    },
    [setValue, setInputError]
  );
  useEffect(() => {
    if (!!inputError) {
      setGroupClass(errorInput);
    } else if (focused) {
      setGroupClass(focusedInput);
    } else {
      setGroupClass('');
    }
  }, [inputError, focused]);

  return (
    <SnackbarProvider>
      <FooterSubscribeWrap>
        <Typography className={subscribeHeader} variant="h3" color="text-invert">
          Будьте в курсе новостей
        </Typography>
        <SubscribeGroup
          groupClass={groupClass}
          onSubmit={onSubmit}
          onFocus={onFocus}
          onMailInput={onMailInput}
          inputError={inputError}
          value={value}
          setFocused={setFocused}
        />
        <div>
          <Typography variant="caption" className={policyTypography} color="text-invert">
            Подписываясь на рассылку, я даю{' '}
            <a
              href={`https://${isNewTheme ? 'lemanapro' : 'leroymerlin'}.ru/personal-data-docs/`}
              rel="noopener noreferrer">
              согласие
            </a>{' '}
            на обработку персональных данных и на получение рекламных сообщений и новостей о товарах и
            услугах. Сайт защищён системой reCAPTCHA, к нему применяется{' '}
            <a href="https://policies.google.com/privacy?hl=ru" rel="noopener noreferrer">
              политика конфиденциальности
            </a>{' '}
            и{' '}
            <a href="https://policies.google.com/terms?hl=ru" rel="noopener noreferrer">
              условия использования Google
            </a>
            .
          </Typography>
        </div>
        {currentSnack && (
          <Snackbar horizontalPosition="end" data-qa={DATA_QA_FOOTER_SUBSCRIBE_SNACKBAR}>
            <Snack
              key={currentSnack.id}
              id={currentSnack.id}
              paragraph={currentSnack.content}
              variant={currentSnack.variant}
              icon={currentSnack.icon}
              header={currentSnack.header}
              autoHideTimer={3000}
              data-qa={DATA_QA_FOOTER_SUBSCRIBE_SNACK}
            />
          </Snackbar>
        )}
      </FooterSubscribeWrap>
    </SnackbarProvider>
  );
};
